import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ListPlus, Eye, EyeOff, Pencil, Megaphone, Share2, Download, Clock, Radio, LogIn, Trash2, Mic, Play, Bell } from "lucide-react";
import { SupervisionViewer } from "@/components/SupervisionViewer";
import { PowerDeleteButton, usePowerDelete } from "@/context/PowerDeleteContext";
import { isAdminPushEnabled, ADMIN_PUSH_PREF_KEY } from "@/lib/notifySound";

const DEPT_LABELS = { team: "Team", book: "Book", help: "Help", official: "Official" };

function ApplicationRow({ app, onDecided }) {
  const [salary, setSalary] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (action) => {
    if (action === "approve" && !salary) {
      toast.error("Set a monthly salary before approving.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/employee-applications/${app.id}/decision`, {
        action, salary: action === "approve" ? Number(salary) : undefined,
      });
      if (action === "approve") {
        if (data.email_sent) {
          toast.success("Employee approved and welcomed");
        } else {
          toast.error("Approved, but the welcome email failed to send. Ask them to check junk mail or contact support.");
        }
      } else {
        toast.success("Application rejected");
      }
      onDecided();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`emp-app-${app.id}`} className="card-soft p-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="font-medium">{app.name} — {DEPT_LABELS[app.department]}</div>
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
          {app.email} · {app.mobile} · @{app.username}
        </div>
        <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{app.experience}</div>
      </div>
      <div className="flex items-center gap-2">
        <input data-testid={`emp-app-salary-${app.id}`} type="number" min={1} placeholder="Salary ₹"
          value={salary} onChange={e => setSalary(e.target.value)}
          className="w-28 rounded-lg border border-[color:var(--border-soft)] px-2 py-1.5 text-sm" />
        <button data-testid={`emp-app-approve-${app.id}`} disabled={busy} onClick={() => decide("approve")}
          className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5" /> Approve
        </button>
        <button data-testid={`emp-app-reject-${app.id}`} disabled={busy} onClick={() => decide("reject")}
          className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
          <XCircle className="w-3.5 h-3.5" /> Reject
        </button>
        <PowerDeleteButton collection="employee_applications" id={app.id} onDeleted={onDecided} />
      </div>
    </div>
  );
}

function SalaryEditor({ employeeId, salary, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(salary ?? "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!value || Number(value) <= 0) {
      toast.error("Enter a valid salary amount.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/employees/${employeeId}/salary`, { salary: Number(value) });
      toast.success(data.email_sent ? "Salary updated — employee notified by email" : "Salary updated, but the notification email failed to send");
      setEditing(false);
      onUpdated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button data-testid={`emp-salary-edit-${employeeId}`} onClick={() => { setValue(salary ?? ""); setEditing(true); }}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--brand)" }}>
        <Pencil className="w-3 h-3" /> Edit salary
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5">
      <input data-testid={`emp-salary-input-${employeeId}`} type="number" min={1} value={value}
        onChange={e => setValue(e.target.value)}
        className="w-24 rounded-lg border border-[color:var(--border-soft)] px-2 py-1 text-xs" />
      <button data-testid={`emp-salary-save-${employeeId}`} disabled={busy} onClick={save}
        className="btn-primary text-xs px-2 py-1">{busy ? "Saving…" : "Save"}</button>
      <button data-testid={`emp-salary-cancel-${employeeId}`} onClick={() => setEditing(false)}
        className="btn-outline text-xs px-2 py-1">Cancel</button>
    </div>
  );
}

function EscalationsViewer({ employeeId }) {
  const { refreshToken } = usePowerDelete();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/escalations`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    load();
  };

  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [refreshToken]);

  return (
    <div>
      <button data-testid={`emp-escalations-toggle-${employeeId}`} onClick={toggle}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--accent-hover)" }}>
        <Megaphone className="w-3.5 h-3.5" /> Escalations
      </button>
      {open && (
        <div data-testid={`emp-escalations-body-${employeeId}`} className="mt-2 space-y-2 w-full basis-full">
          {items && items.length === 0 && (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No escalations yet.</div>
          )}
          {items && items.map(it => (
            <div key={it.id} data-testid={`emp-escalation-${it.id}`} className="text-xs rounded-xl p-3 flex items-start justify-between gap-2"
              style={{ background: "rgba(196,90,60,0.08)" }}>
              <div>
                <div style={{ color: "var(--text-secondary)" }}>{new Date(it.created_at).toLocaleString()}</div>
                <div className="mt-1 whitespace-pre-wrap">{it.message}</div>
              </div>
              <PowerDeleteButton collection="escalations" id={it.id}
                onDeleted={() => setItems(prev => prev.filter(x => x.id !== it.id))} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function VoiceNotesViewer({ employeeId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/voice-notes`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    load();
  };

  const play = async (note) => {
    try {
      const res = await api.get(`/admin/voice-notes/${note.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPlayingId(note.id);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.onended = async () => {
          await api.post(`/admin/voice-notes/${note.id}/mark-fully-played`).catch(() => {});
          setItems(prev => (prev || []).filter(x => x.id !== note.id));
          setPlayingId(null);
          URL.revokeObjectURL(url);
        };
        try {
          await audioRef.current.play();
        } catch (playErr) {
          URL.revokeObjectURL(url);
          setPlayingId(null);
          if (playErr?.name === "NotSupportedError") {
            toast.error("This browser can't play this voice note's audio format. Try opening it in Chrome or Firefox instead.");
          } else {
            toast.error(formatApiError(playErr));
          }
        }
      }
    } catch (e) { toast.error(formatApiError(e)); setPlayingId(null); }
  };

  return (
    <div>
      <button data-testid={`emp-voice-toggle-${employeeId}`} onClick={toggle}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--brand)" }}>
        <Mic className="w-3.5 h-3.5" /> Voice messages
      </button>
      {open && (
        <div data-testid={`emp-voice-body-${employeeId}`} className="mt-2 space-y-2 w-full basis-full">
          {items && items.length === 0 && (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No voice messages.</div>
          )}
          {items && items.map(it => (
            <div key={it.id} data-testid={`emp-voice-${it.id}`}
              className="text-xs rounded-xl p-3 flex items-center justify-between gap-2" style={{ background: "rgba(91,117,83,0.08)" }}>
              <div style={{ color: "var(--text-secondary)" }}>{new Date(it.created_at).toLocaleString()}</div>
              <button data-testid={`emp-voice-play-${it.id}`} disabled={playingId === it.id}
                onClick={() => play(it)} className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1">
                <Play className="w-3.5 h-3.5" /> {playingId === it.id ? "Playing…" : "Play"}
              </button>
            </div>
          ))}
          <audio ref={audioRef} data-testid="admin-voice-audio-player" className="hidden" />
        </div>
      )}
    </div>
  );
}

function SharesViewer({ employeeId }) {
  const { refreshToken } = usePowerDelete();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/shares`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggle = () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    load();
  };

  useEffect(() => { if (open) load(); /* eslint-disable-line */ }, [refreshToken]);

  const download = async (share) => {
    setDownloadingId(share.id);
    try {
      const res = await api.get(`/admin/employees/shares/${share.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = share.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDownloadingId(null); }
  };

  return (
    <div>
      <button data-testid={`emp-shares-toggle-${employeeId}`} onClick={toggle}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--brand)" }}>
        <Share2 className="w-3.5 h-3.5" /> Shared files
      </button>
      {open && (
        <div data-testid={`emp-shares-body-${employeeId}`} className="mt-2 space-y-2 w-full basis-full">
          {items && items.length === 0 && (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No shared files yet.</div>
          )}
          {items && items.map(it => (
            <div key={it.id} data-testid={`emp-share-${it.id}`}
              className="text-xs rounded-xl p-3 flex items-center justify-between gap-2" style={{ background: "rgba(91,117,83,0.08)" }}>
              <div>
                <div>{it.original_filename}</div>
                <div style={{ color: "var(--text-secondary)" }}>{formatFileSize(it.size)} · {new Date(it.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button data-testid={`emp-share-download-${it.id}`} disabled={downloadingId === it.id}
                  onClick={() => download(it)} className="btn-outline text-xs px-2 py-1 inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> {downloadingId === it.id ? "…" : "Download"}
                </button>
                <PowerDeleteButton collection="employee_shares" id={it.id}
                  onDeleted={() => setItems(prev => prev.filter(x => x.id !== it.id))} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDuration(seconds) {
  if (seconds == null) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function SessionsViewer({ employeeId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (items) return;
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/sessions`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <button data-testid={`emp-sessions-toggle-${employeeId}`} onClick={toggle}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--brand)" }}>
        <Clock className="w-3.5 h-3.5" /> Login sessions
      </button>
      {open && (
        <div data-testid={`emp-sessions-body-${employeeId}`} className="mt-2 space-y-1 w-full basis-full">
          {items && items.length === 0 && (
            <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No sessions recorded yet.</div>
          )}
          {items && items.map(s => (
            <div key={s.id} className="text-xs flex items-center justify-between gap-2 rounded-lg px-3 py-1.5" style={{ background: "rgba(91,117,83,0.06)" }}>
              <span>{new Date(s.login_at).toLocaleString()}</span>
              <span style={{ color: "var(--text-secondary)" }}>{s.logout_at ? formatDuration(s.duration_seconds) : `${formatDuration(s.duration_seconds)} (ongoing)`}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GuideForm({ employeeId, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File is too large (max 10MB)"); return; }
    setUploading(true);
    try {
      const body = new FormData(); body.append("file", file);
      const { data } = await api.post("/uploads/document", body, { headers: { "Content-Type": "multipart/form-data" } });
      setFileUrl(data.url);
      toast.success("Guide file uploaded");
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setUploading(false); }
  };

  const assign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/employees/${employeeId}/tasks`, { title, description_file_url: fileUrl || null });
      toast.success("Task assigned");
      setTitle(""); setFileUrl(""); setOpen(false);
      onAssigned();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button data-testid={`emp-guide-open-${employeeId}`} onClick={() => setOpen(true)}
        className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
        <ListPlus className="w-3.5 h-3.5" /> Guide
      </button>
    );
  }
  return (
    <form onSubmit={assign} className="flex flex-wrap items-center gap-2 mt-2">
      <input required data-testid={`emp-guide-title-${employeeId}`} placeholder="Task title" value={title}
        onChange={e => setTitle(e.target.value)}
        className="rounded-lg border border-[color:var(--border-soft)] px-2 py-1.5 text-sm" />
      <input type="file" accept=".pdf,image/*" data-testid={`emp-guide-file-${employeeId}`}
        onChange={pickFile} className="hidden" id={`emp-guide-file-input-${employeeId}`} />
      <label htmlFor={`emp-guide-file-input-${employeeId}`} className="btn-outline text-xs px-3 py-1.5 cursor-pointer">
        {uploading ? "Uploading…" : fileUrl ? "File attached ✓" : "Upload description file (optional)"}
      </label>
      <button data-testid={`emp-guide-submit-${employeeId}`} disabled={busy || uploading || !title.trim()} className="btn-primary text-xs px-3 py-1.5 disabled:opacity-40">
        {busy ? "Assigning…" : "Assign"}
      </button>
    </form>
  );
}

function Form2Viewer({ employeeId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    try {
      const { data: d } = await api.get(`/admin/employees/${employeeId}/form2`);
      setData(d);
    } catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div>
      <button data-testid={`emp-form2-toggle-${employeeId}`} onClick={toggle}
        className="text-xs underline inline-flex items-center gap-1" style={{ color: "var(--brand)" }}>
        {open ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} Form 2 (sensitive)
      </button>
      {open && (
        <div data-testid={`emp-form2-body-${employeeId}`} className="mt-2 text-xs rounded-xl p-3 space-y-1"
          style={{ background: "rgba(91,117,83,0.08)" }}>
          {err && <div style={{ color: "var(--accent)" }}>{err}</div>}
          {data && (
            <>
              <div>Aadhaar: {data.aadhaar}</div>
              <div>PAN: {data.pan}</div>
              <div>UPI: {data.upi_id}</div>
              <div>Alt. mobile: {data.alternate_mobile}</div>
              <div>Address: {data.address}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Module-level (not per-instance) so remounts of the panel — e.g. switching
// admin dashboard tabs — don't lose track of who was already known to be
// online and cause duplicate/missed "just logged in" toasts.
const _onlineMapCache = {};
let _loadInFlight = false;

function PushNotificationToggle() {
  const [enabled, setEnabled] = useState(isAdminPushEnabled());

  const toggle = async (checked) => {
    if (checked) {
      if (typeof Notification === "undefined") {
        toast.error("This browser doesn't support desktop notifications.");
        return;
      }
      if (Notification.permission === "denied") {
        toast.error(
          "Notifications are blocked for this site in your browser settings. Click the padlock/info icon next to the address bar → Site settings → Notifications → Allow, then try again.",
          { duration: 8000 }
        );
        return;
      }
      const perm = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
      if (perm !== "granted") {
        toast.error("Notification permission was not granted.");
        return;
      }
      localStorage.setItem(ADMIN_PUSH_PREF_KEY, "1");
      setEnabled(true);
      toast.success("Desktop push notifications enabled for employee messages");
    } else {
      localStorage.setItem(ADMIN_PUSH_PREF_KEY, "0");
      setEnabled(false);
    }
  };

  return (
    <label data-testid="admin-push-toggle-label" className="inline-flex items-center gap-2 text-sm cursor-pointer mb-4">
      <input type="checkbox" data-testid="admin-push-toggle" checked={enabled}
        onChange={e => toggle(e.target.checked)} className="w-4 h-4" style={{ accentColor: "var(--brand)" }} />
      <Bell className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
      Desktop push notifications for employee messages
    </label>
  );
}

export default function EmployeeAdminPanel() {
  const { refreshToken } = usePowerDelete();
  const [apps, setApps] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tasksByEmployee, setTasksByEmployee] = useState({});
  const [supervising, setSupervising] = useState(null); // {id, name, mode}

  const load = async () => {
    if (_loadInFlight) return;
    _loadInFlight = true;
    try {
      const [{ data: a }, { data: e }] = await Promise.all([
        api.get("/admin/employee-applications"),
        api.get("/admin/employees"),
      ]);
      setApps(a.filter(x => x.status === "pending"));
      e.forEach(emp => {
        if (emp.is_online && _onlineMapCache[emp.id] === false) {
          toast.info(`${emp.name} just logged in`);
        }
        _onlineMapCache[emp.id] = emp.is_online;
      });
      setEmployees(e);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { _loadInFlight = false; }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 20000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [refreshToken]);

  const loadTasks = async (employeeId) => {
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/tasks`);
      setTasksByEmployee(prev => ({ ...prev, [employeeId]: data }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const impersonateEmployee = async (emp) => {
    try {
      await api.post(`/admin/impersonate/${emp.id}`);
      toast.success(`Now viewing as ${emp.name}`);
      window.location.href = "/employee";
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteEmployee = async (emp) => {
    if (!window.confirm(`Delete ${emp.name} (${emp.email})? They'll move to the recovery bin and can be restored later.`)) return;
    try {
      await api.delete(`/users/${emp.id}`);
      toast.success(`Deleted ${emp.email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="employee-admin-panel" className="mt-12">
      <h2 className="font-serif-display text-2xl mb-4">Internal Team</h2>
      <PushNotificationToggle />

      {apps.length > 0 && (
        <div className="mb-8">
          <div className="label-eyebrow mb-3">Pending applications</div>
          <div className="space-y-3">
            {apps.map(app => <ApplicationRow key={app.id} app={app} onDecided={load} />)}
          </div>
        </div>
      )}

      <div className="label-eyebrow mb-3">Employees</div>
      <div className="space-y-4">
        {employees.length === 0 && (
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>No employees yet.</p>
        )}
        {employees.map(emp => (
          <div key={emp.id} data-testid={`emp-card-${emp.id}`} className="card-soft p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-medium">{emp.name} — {DEPT_LABELS[emp.department]}</div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  Serve ID: {emp.email} · Personal: {emp.personal_email} · @{emp.username} · ₹{emp.salary?.toLocaleString("en-IN")}/mo
                  {" · "}
                  <SalaryEditor employeeId={emp.id} salary={emp.salary} onUpdated={load} />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span data-testid={`emp-online-${emp.id}`}
                  className="text-xs px-2 py-1 rounded-full inline-flex items-center gap-1"
                  style={{
                    background: emp.is_online ? "rgba(91,117,83,0.15)" : "rgba(0,0,0,0.06)",
                    color: emp.is_online ? "var(--brand)" : "var(--text-secondary)",
                  }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: emp.is_online ? "#4a7a3f" : "#999" }} />
                  {emp.is_online ? "Online" : "Offline"}
                </span>
                <span data-testid={`emp-payment-status-${emp.id}`}
                  className="text-xs px-2 py-1 rounded-full capitalize"
                  style={{
                    background: emp.payment_status === "cleared" ? "rgba(91,117,83,0.15)" : "rgba(196,90,60,0.12)",
                    color: emp.payment_status === "cleared" ? "var(--brand)" : "var(--accent)",
                  }}>
                  {emp.payment_status}
                </span>
                <div className="flex flex-col items-start gap-1">
                  <button data-testid={`emp-supervise-${emp.id}`} disabled={!emp.is_online}
                    onClick={() => setSupervising({ id: emp.id, name: emp.name, mode: "audio" })}
                    title={emp.is_online ? "Listen to their mic live" : "Employee is offline"}
                    className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Radio className="w-3.5 h-3.5" /> Supervise
                  </button>
                  <button data-testid={`emp-watch-${emp.id}`} disabled={!emp.is_online}
                    onClick={() => setSupervising({ id: emp.id, name: emp.name, mode: "video" })}
                    title={emp.is_online ? "Turn on their camera & watch live" : "Employee is offline"}
                    className="text-xs px-3 inline-flex items-center gap-1 underline disabled:opacity-40 disabled:cursor-not-allowed"
                    style={{ color: "var(--brand)" }}>
                    <Eye className="w-3 h-3" /> Watch
                  </button>
                </div>
                <GuideForm employeeId={emp.id} onAssigned={() => loadTasks(emp.id)} />
                <button data-testid={`emp-tasks-toggle-${emp.id}`} onClick={() => loadTasks(emp.id)}
                  className="btn-outline text-xs px-3 py-1.5">
                  {emp.open_tasks} open task{emp.open_tasks === 1 ? "" : "s"}
                </button>
                <button data-testid={`emp-view-as-${emp.id}`} onClick={() => impersonateEmployee(emp)}
                  className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
                  <LogIn className="w-3.5 h-3.5" /> View as
                </button>
                <button data-testid={`emp-delete-${emp.id}`} onClick={() => deleteEmployee(emp)}
                  className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1"
                  style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
                <PowerDeleteButton collection="users" id={emp.id} onDeleted={load} />
              </div>
            </div>
            <div className="mt-2">
              <Form2Viewer employeeId={emp.id} />
            </div>
            <div className="mt-2 flex flex-wrap items-start gap-4">
              <EscalationsViewer employeeId={emp.id} />
              <SharesViewer employeeId={emp.id} />
              <VoiceNotesViewer employeeId={emp.id} />
              <SessionsViewer employeeId={emp.id} />
            </div>
            {tasksByEmployee[emp.id] && (
              <div className="mt-3 space-y-1">
                {tasksByEmployee[emp.id].map(t => (
                  <div key={t.id} className="text-xs flex items-center justify-between gap-2" style={{ color: "var(--text-secondary)" }}>
                    <span>{t.title}{t.description_file_url && (
                      <a href={t.description_file_url} target="_blank" rel="noreferrer" className="underline ml-2" style={{ color: "var(--brand)" }}>guide file ↗</a>
                    )}</span>
                    <span className="capitalize">{t.status}</span>
                  </div>
                ))}
                {tasksByEmployee[emp.id].length === 0 && (
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No tasks yet.</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {supervising && (
        <SupervisionViewer employeeId={supervising.id} employeeName={supervising.name} mode={supervising.mode} onClose={() => setSupervising(null)} />
      )}
    </div>
  );
}
